// Use the same code for both client and client-operator
// Copy file after each change

// import "context-filter-polyfill";
import { TemplateTextSection, TicketTemplate } from "../shared/interfaces";
import {
  COLORS_PARTIAL,
  DownloadData,
  FontFamilyTypeEnum_PARTIAL,
  MomentHeaderData,
  formatTicketGameDate,
  loadImage,
  releaseCanvas,
  uploadToS3ServerLargeImage,
  urltoFile,
} from "./sharedFunctions";
import { ensureTemplateFontsForFamilies } from "./ticketFontLoader";

const TICKET_TEXT_SECTION_KEYS = [
  "section", "row", "seat", "scoreTeam1", "scoreTeam2", "date", "stadium", "city",
  "team1Abbreviation", "team2Abbreviation", "momentText",
];

function collectTemplateFontFamilies(template: TicketTemplate): string[] {
  return Array.from(new Set(
    TICKET_TEXT_SECTION_KEYS
      .map((key) => template[key]?.font)
      .filter((font): font is string => typeof font === "string" && font.trim().length > 0),
  ));
}

/** GA name box: explicit gaPlacement wins; else legacy row-if-visible. */
function resolveGaPlacementSlot(template: TicketTemplate): "row" | "section" {
  if (template?.row?.gaPlacement && !template.row.hidden) {
    return "row";
  }
  if (template?.section?.gaPlacement && !template.section.hidden) {
    return "section";
  }
  if (template?.row && !template.row.hidden) {
    return "row";
  }
  return "section";
}

function prepareTextValues(
  headerData: MomentHeaderData,
  selectedSection: string,
  selectedRow: string,
  selectedSeat: string,
  selectedSuite: string,
) {
  const dateText = headerData.selectedGameData
    ? formatTicketGameDate(headerData.selectedGameData.startDate)
    : "";
  return {
    section: selectedSection,
    row: selectedRow,
    seat: selectedSeat,
    suite: selectedSuite,
    scoreTeam1: (headerData.moment?.team1Score || headerData.moment?.team1Score === 0) ? headerData.moment.team1Score : "",
    scoreTeam2: (headerData.moment?.team2Score || headerData.moment?.team2Score === 0) ? headerData.moment.team2Score : "",
    date: dateText,
    stadium: headerData.selectedGameData?.stadium || "",
    city: headerData.selectedGameData?.city || "",
    team1Abbreviation: headerData.selectedGameData?.team1?.teamAbbreviation || "",
    team2Abbreviation: headerData.selectedGameData?.team2?.teamAbbreviation || "",
    momentText: headerData.moment?.text || "",
  };
}

function renderTextSection(context, template: TicketTemplate, key, value) {
  const section: TemplateTextSection = template[key];
  if (!section || section.hidden) {
    return;
  }

  const x = Number(section.x || 0);
  const yCenter = Number(section.y || 0);
  const width = Number(section.width || 0);
  const height = Number(section.height || 0);
  const yTop = yCenter - height / 2;

  if (section.backgroundColor && section.backgroundColor !== "none") {
    context.beginPath();
    context.fillStyle = section.backgroundColor;
    context.strokeStyle = section.backgroundColor;
    context.beginPath();
    context.rect(x, yTop, width, height);
    context.fill();
    context.closePath();
  }

  if (!value) {
    return;
  }

  context.beginPath();

  context.fillStyle = section.color || COLORS_PARTIAL.WHITE;
  const fontSize = Number(section.fontSize) || 16;
  const fontBold = Number(section.fontBold) || 600;
  context.font = `${fontBold} ${fontSize}px ${section.font || FontFamilyTypeEnum_PARTIAL.POPPINS}`;
  context.textAlign = "center";
  context.textBaseline = "middle";
  context.fillText(value, x + width / 2, yCenter);

  context.closePath();
}

export interface ImageWithSizes {
  dataUrl: string;
  width: number;
  height: number;
}

export async function prepareTemplateImage(
  imageLink: string,
  templateImageLink: string,
  template: TicketTemplate,
  headerData: MomentHeaderData,
  selectedSection: string,
  selectedRow: string,
  selectedSeat: string,
  selectedSuite: string,
  imageAreaLeft?: number,
  imageAreaTop?: number,
  imageAreaWidth?: number,
  imageAreaHeight?: number,
  blurImage?: boolean,
  showWatermark?: boolean,
  watermarkLogoOpacity = 0.4,
  /** General admission / special section: show section name in row slot (center column); no separate row/seat values. */
  generalAdmissionTicket?: boolean,
): Promise<ImageWithSizes | null> {
  if (!imageLink || !template || !templateImageLink) {
    return null;
  }

  const imageResult = await loadImage(imageLink, false);
  if (!imageResult?.image?.width) {
    return null;
  }

  const templateImageResult = await loadImage(templateImageLink, false);
  if (!templateImageResult?.image?.width) {
    return null;
  }

  const { image } = imageResult;
  const templateImage = templateImageResult.image;
  const templateWidth = templateImage.width;
  const templateHeight = templateImage.height;

  const canvas = document.createElement("canvas");
  canvas.width = templateWidth;
  canvas.height = templateHeight;
  const context = canvas.getContext("2d");

  if (!context) {
    console.log("No memory for canvas");
    return null;
  }

  // place image inside image rect according with its scale
  if (template.imagePosition?.width && template.imagePosition?.height) {
    const width = Number(template.imagePosition.width || 0);
    const height = Number(template.imagePosition.height || 0);
    const x = Number(template.imagePosition.x || 0);
    const y = Number(template.imagePosition.y || 0);

    const imageWidth = imageAreaWidth || image.width;
    const imageHeight = imageAreaHeight || image.height;
    const xScale = width / imageWidth;
    const yScale = height / imageHeight;
    const xWidth = xScale < yScale ? width : imageWidth * yScale;
    const yHeight = xScale < yScale ? imageHeight * xScale : height;
    const xOffset = (x || 0) + (width - xWidth) / 2.0;
    const yOffset = (y || 0) + (height - yHeight) / 2.0;

    if (blurImage) {
      context.filter = "blur(5px)";
      context.drawImage(
        image,
        imageAreaLeft || 0,
        imageAreaTop || 0,
        imageWidth,
        imageHeight,
        xOffset,
        yOffset,
        xWidth,
        yHeight,
      );
      context.filter = "none";
      context.drawImage(
        image,
        imageAreaLeft || 0,
        (imageAreaTop || 0) + imageHeight * 0.25,
        imageWidth,
        imageHeight * 0.5,
        xOffset,
        yOffset + yHeight * 0.25,
        xWidth,
        yHeight * 0.5,
      );
    } else {
      context.drawImage(
        image,
        imageAreaLeft || 0,
        imageAreaTop || 0,
        imageWidth,
        imageHeight,
        xOffset,
        yOffset,
        xWidth,
        yHeight,
      );
    }

    // watermark
    if (showWatermark && headerData.team1SponsorLogoImage?.width) {
      const sponsorImage = headerData.team1SponsorLogoImage;
      context.globalAlpha = watermarkLogoOpacity;

      const rate = sponsorImage.width / sponsorImage.height;
      const partWidth = xWidth / 12; // You said you want it half the size, changed from /6 to /12
      const partHeight = partWidth / rate;
      const spacing = 40;

      for (let wx = 0; wx < xWidth; wx += partWidth + spacing) {
        for (let wy = 0; wy < yHeight; wy += partHeight + spacing) {
          context.save();
          context.translate(xOffset + wx + partWidth / 2, yOffset + wy + partHeight / 2);
          context.rotate((45 * Math.PI) / 180);
          context.drawImage(
            sponsorImage,
            -partWidth / 2,
            -partHeight / 2,
            partWidth,
            partHeight,
          );
          context.restore();
        }
      }

      context.globalAlpha = 1;
    }
  }

  // draw main template image
  context.drawImage(
    templateImage,
    0,
    0,
    templateWidth,
    templateHeight,
    0,
    0,
    templateWidth,
    templateHeight,
  );

  // render texts
  await ensureTemplateFontsForFamilies(collectTemplateFontFamilies(template));
  const texts = prepareTextValues(
    headerData, selectedSection, selectedRow, selectedSeat, selectedSuite,
  );
  if (selectedSuite) {
    renderTextSection(context, template, "section", "STE");

    // Extract suite number from texts.suite (e.g. "suite 19" → "19")
    const suiteNumberMatch = texts.suite.match(/\d+/);
    const suiteNumber = suiteNumberMatch ? suiteNumberMatch[0] : "";

    renderTextSection(context, template, "row", suiteNumber);
    renderTextSection(context, template, "seat", "VIP");
  } else if (generalAdmissionTicket) {
    // Place GA name in Section or Row based on template.gaPlacement (legacy: row if visible).
    const gaName = texts.section;
    const slot = resolveGaPlacementSlot(template);
    renderTextSection(context, template, slot, gaName);
  } else {
    renderTextSection(context, template, "section", texts.section);
    renderTextSection(context, template, "row", texts.row);
    renderTextSection(context, template, "seat", texts.seat);
  }
  renderTextSection(context, template, "scoreTeam1", texts.scoreTeam1);
  renderTextSection(context, template, "scoreTeam2", texts.scoreTeam2);
  renderTextSection(context, template, "date", texts.date);
  renderTextSection(context, template, "stadium", texts.stadium);
  renderTextSection(context, template, "city", texts.city);
  renderTextSection(context, template, "team1Abbreviation", texts.team1Abbreviation);
  renderTextSection(context, template, "team2Abbreviation", texts.team2Abbreviation);
  renderTextSection(context, template, "momentText", texts.momentText);

  if (template?.team1Image && !template.team1Image.hidden) {
    if (headerData.team1LogoImage && headerData.team1LogoImage.width) {
      const section = template.team1Image;
      context.drawImage(
        headerData.team1LogoImage,
        Number(section.x),
        Number(section.y),
        Number(section.width),
        Number(section.height),
      );
    }
  }

  if (template?.team2Image && !template.team2Image.hidden) {
    if (headerData.team2LogoImage && headerData.team2LogoImage.width) {
      const section = template.team2Image;
      context.drawImage(
        headerData.team2LogoImage,
        Number(section.x),
        Number(section.y),
        Number(section.width),
        Number(section.height),
      );
    }
  }

  const dataUrl = canvas.toDataURL("image/png");
  releaseCanvas(canvas);

  return {
    dataUrl,
    width: templateWidth,
    height: templateHeight,
  };
}

export async function prepareAndSaveTicketImage(
  imageData: DownloadData,
  ticketTemplateImageKey: string,
  template: TicketTemplate,
  selectedSection: string,
  selectedRow: string,
  selectedSeat: string,
  selectedSuite: string,
  onlyPrepare = false,
  headerData?: MomentHeaderData,
  s3Folder?: string,
  filename?: string,
  blurImage?: boolean,
  showWatermark?: boolean,
  watermarkLogoOpacity = 0.4,
  generalAdmissionTicket?: boolean,
): Promise<ImageWithSizes | null> {
  const base64ImageData = await prepareTemplateImage(
    imageData.link,
    ticketTemplateImageKey,
    template,
    headerData,
    selectedSection,
    selectedRow,
    selectedSeat,
    selectedSuite,
    imageData.x,
    imageData.y,
    imageData.width,
    imageData.height,
    blurImage,
    showWatermark,
    watermarkLogoOpacity,
    generalAdmissionTicket,
  );

  if (!base64ImageData) {
    return null;
  }

  if (onlyPrepare) {
    // no need to save to server
    return base64ImageData;
  }

  const file = await urltoFile(base64ImageData.dataUrl, "momento.png", "image/png");
  const fileName = filename || `${Date.now()}.png`;

  const result = await uploadToS3ServerLargeImage(file, fileName, "image/png", s3Folder);
  if (!result) {
    return null;
  }

  return {
    dataUrl: result,
    width: base64ImageData.width,
    height: base64ImageData.height,
  };
}
